import { Controller } from '@hotwired/stimulus'

const validateForm = (form, callback) => {
  /*
    Using setTimeout as the deferred execution technique.
    It runs the setTimeout's callback function when the Stack becomes empty, in the next event loop
    This means that all event handlers from ClientSideValidations have been processed, allowing functioning correctly

    Links:
      - (1) https://github.com/DavyJonesLocker/client_side_validations/blob/e5f60a41cf4283d542676353aa82f40b589f88ba/src/index.js#L187
      - (2) https://github.com/DavyJonesLocker/client_side_validations/blob/e5f60a41cf4283d542676353aa82f40b589f88ba/src/core.js#L49-L51
  */
  return setTimeout(() => {
    const $form = $(form)
    $form.validate()
    const validators = form.ClientSideValidations.settings.validators
    const isValid = $form.isValid(validators)

    return callback(isValid)
  }, 0)
}

// Disable form inputs to prevent creating duplicate records
// Avoid using the "disabled" attribute as any fields marked as "disabled" will not be sent to the server
// https://github.com/fishisfast/Fishis-Fast/pull/13241#issuecomment-1696056742
const disableFields = (fields) => {
  fields.forEach((fld) => fld.setAttribute('readonly', true))
}
const enableFields = (fields) => {
  fields.forEach((fld) => fld.setAttribute('readonly', false))
}

export default class extends Controller {
  static targets = ['field']

  initialize() {
    this.turboFrame = this.element.closest('turbo-frame')
    this.modal = this.element.closest('div.modal')
    this.form = this.element.querySelector('form')

    this.showLoadingOnInit = this.showLoadingOnInit.bind(this)
    this.showStatusOnCompleteFormRequest =
      this.showStatusOnCompleteFormRequest.bind(this)
    this.showErrorIfRequestFailed = this.showErrorIfRequestFailed.bind(this)
    this.initValidation = this.initValidation.bind(this)

    //https://turbo.hotwired.dev/reference/events
    this.turboFrame.addEventListener(
      'turbo:submit-start',
      this.showLoadingOnInit
    )
    this.turboFrame.addEventListener(
      'turbo:submit-end',
      this.showStatusOnCompleteFormRequest
    )
    // Fetch request fails due to network errors rather than business logic errors
    this.turboFrame.addEventListener(
      'turbo:fetch-request-error',
      this.showErrorIfRequestFailed
    )
    // When the controller is within an opening modal, we need to wait until all inputs in the modal become visible
    // https://github.com/DavyJonesLocker/client_side_validations/blob/e5f60a41cf4283d542676353aa82f40b589f88ba/src/core.js#L225
    // https://github.com/DavyJonesLocker/client_side_validations/blob/e5f60a41cf4283d542676353aa82f40b589f88ba/src/core.js#L103
    if (this.modal !== null) {
      this.modal.addEventListener('shown.bs.modal', this.initValidation)
    }
  }

  connect() {
    this.initValidation()
  }

  disconnect() {
    this.turboFrame.removeEventListener(
      'turbo:submit-start',
      this.showLoadingOnInit
    )
    this.turboFrame.removeEventListener(
      'turbo:submit-end',
      this.showStatusOnCompleteFormRequest
    )
    // Fetch request fails due to network errors rather than business logic errors
    this.turboFrame.removeEventListener(
      'turbo:fetch-request-error',
      this.showErrorIfRequestFailed
    )
    if (this.modal !== null) {
      this.modal.removeEventListener('shown.bs.modal', this.initValidation)
    }
  }

  changeInput() {
    validateForm(this.form, (isValid) => {
      if (isValid) {
        disableFields(this.fieldTargets)
        Rails.fire(this.form, 'submit')
      } else {
        this.dispatch('error')
      }
    })
  }

  removeNewLine(event) {
    event.preventDefault()
    this.turboFrame.replaceChildren()
  }

  initValidation() {
    const $form = $(this.form)
    $form.validate()
    const validators = this.form.ClientSideValidations.settings.validators
    const isValid = $form.isValid(validators)

    if (!isValid) {
      this.dispatch('error')
    }
  }

  showLoadingOnInit() {
    this.dispatch('loading')
  }

  showStatusOnCompleteFormRequest({ detail: { success, fetchResponse } }) {
    if (success === undefined) {
      this.dispatch('loading')
    } else if (fetchResponse.redirected) {
      // While regular updates do not cause redirects, an expired user session can
      this.dispatch('error')
    } else if (success === true) {
      this.dispatch('success')
      enableFields(this.fieldTargets)
    } else {
      this.dispatch('error')
      enableFields(this.fieldTargets)
    }
  }

  showErrorIfRequestFailed() {
    this.dispatch('error')
    enableFields(this.fieldTargets)
  }
}
